import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock, ListChecks } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { ArticleRenderer } from "../components/blog/ArticleRenderer";
import { AuthorBox } from "../components/blog/AuthorBox";
import { getArticle, ARTICLES } from "../content/blog";
import { AUTHOR, REVIEWER, VENUE, SITE } from "../content/blog/authors";
import NotFound from "./NotFound";

const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const stripHtml = (s) => s.replace(/<[^>]+>/g, "");

const buildJsonLd = (a) => {
  const url = `${SITE}/blog/${a.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        headline: a.h1,
        description: a.metaDescription,
        image: `${SITE}${a.hero.src}`,
        datePublished: a.datePublished,
        dateModified: a.dateModified,
        author: { "@id": `${SITE}/#${AUTHOR.id}` },
        contributor: { "@id": `${SITE}/#${REVIEWER.id}` },
        publisher: { "@id": `${SITE}/#org` },
        mainEntityOfPage: url,
        articleSection: a.category,
      },
      {
        "@type": "Person",
        "@id": `${SITE}/#${AUTHOR.id}`,
        name: AUTHOR.name,
        jobTitle: AUTHOR.jobTitle,
        description: AUTHOR.credentials,
        knowsAbout: ["HYROX training", "hybrid racing", "exercise science"],
        sameAs: AUTHOR.sameAs,
      },
      {
        "@type": "Person",
        "@id": `${SITE}/#${REVIEWER.id}`,
        name: REVIEWER.name,
        jobTitle: REVIEWER.jobTitle,
        description: REVIEWER.credentials,
        knowsAbout: ["HYROX racing", "race pacing"],
        sameAs: REVIEWER.sameAs,
      },
      {
        "@type": "SportsActivityLocation",
        "@id": `${SITE}/#org`,
        name: "Hyrox Ninja — HYROX Training Dallas",
        url: SITE,
        address: {
          "@type": "PostalAddress",
          streetAddress: VENUE.street,
          addressLocality: VENUE.city,
          addressRegion: VENUE.state,
          postalCode: VENUE.zip,
          addressCountry: "US",
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: a.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: stripHtml(f.a) },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
          { "@type": "ListItem", position: 3, name: a.h1, item: url },
        ],
      },
    ],
  };
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (!article) return;
    let script = document.getElementById("blog-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "blog-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildJsonLd(article));
    return () => {
      document.getElementById("blog-jsonld")?.remove();
    };
  }, [article]);

  if (!article) return <NotFound />;

  const toc = article.body.filter((b) => b.t === "h2");
  const related = article.related.map(getArticle).filter(Boolean);

  return (
    <div data-testid="blog-article-page" className="pt-16">
      <Seo title={article.metaTitle} description={article.metaDescription} />

      <section className="relative overflow-hidden">
        <img src={article.hero.src} alt={article.hero.alt} className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-3xl mx-auto px-5 lg:px-8 pt-16 pb-10">
          <nav className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-6 flex flex-wrap gap-1.5" data-testid="article-breadcrumb">
            <Link to="/" className="hover:text-volt transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-volt transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-zinc-400">{article.category}</span>
          </nav>
          <h1 className="font-head font-black uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-[2.75rem] mb-6" data-testid="article-h1">
            {article.h1}
          </h1>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 flex flex-wrap gap-x-5 gap-y-1.5" data-testid="article-byline">
            <span>By <span className="text-white">{AUTHOR.name}</span></span>
            <span>Reviewed by <span className="text-white">{REVIEWER.name}</span></span>
            <span>Updated {fmtDate(article.dateModified)}</span>
            <span className="flex items-center gap-1.5"><Clock size={11} className="text-volt" /> {article.readTime} min read</span>
          </p>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-5 lg:px-8 pb-10">
        <aside className="border-l-2 border-volt bg-volt/5 p-6 mb-8" data-testid="article-summary">
          <p className="eyebrow mb-2">The Short Answer</p>
          <p className="text-white leading-relaxed">{article.summary}</p>
        </aside>

        <div className="border border-white/10 bg-[#0c0c0c] p-6 mb-8" data-testid="article-takeaways">
          <p className="eyebrow mb-3 flex items-center gap-2"><ListChecks size={13} /> Key Takeaways</p>
          <ul className="space-y-2.5 article-body">
            {article.takeaways.map((t, i) => (
              <li key={i} className="flex gap-3 text-zinc-300 text-sm leading-relaxed">
                <span className="text-volt font-mono2 shrink-0">›</span>
                <span dangerouslySetInnerHTML={{ __html: t }} />
              </li>
            ))}
          </ul>
        </div>

        <nav className="mb-2" data-testid="article-toc">
          <p className="eyebrow mb-3">On This Page</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {toc.map((h) => (
              <a key={h.id} href={`#${h.id}`} className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 hover:text-volt transition-colors">
                {h.text}
              </a>
            ))}
          </div>
        </nav>

        <ArticleRenderer blocks={article.body} slug={article.slug} />

        <section className="mt-14" data-testid="article-faq" id="faq">
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-6">FAQ</h2>
          <div className="border border-white/10 divide-y divide-white/10">
            {article.faqs.map((f, i) => (
              <details key={i} className="group bg-[#0c0c0c] open:bg-[#101010]" data-testid={`article-faq-${i}`} open={i === 0}>
                <summary className="cursor-pointer list-none px-6 py-4 font-head font-bold uppercase text-base flex items-start justify-between gap-4 hover:text-volt transition-colors">
                  {f.q}
                  <span className="text-volt font-mono2 shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="article-body px-6 pb-5 text-zinc-400 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />
              </details>
            ))}
          </div>
        </section>

        <div className="mt-10" data-testid="article-sources">
          <p className="eyebrow mb-2">Sources</p>
          <ol className="space-y-1">
            {article.sources.map((s, i) => (
              <li key={i} className="font-mono2 text-[11px] text-zinc-500 tracking-wide">{i + 1}. {s}</li>
            ))}
          </ol>
        </div>

        <AuthorBox />
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="article-related">
          <p className="eyebrow mb-4">Keep Reading</p>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="group bg-[#0c0c0c] p-6 hover:bg-[#101010] transition-colors" data-testid={`related-card-${r.slug}`}>
                <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-zinc-500">{r.category}</span>
                <h3 className="font-head font-bold uppercase text-lg leading-tight mt-2 mb-3 group-hover:text-volt transition-colors">{r.h1}</h3>
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-volt inline-flex items-center gap-1.5">Read <ArrowRight size={11} /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="bg-volt text-black py-14" data-testid="article-cta">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-head font-black uppercase tracking-tight text-2xl sm:text-3xl mb-2">{article.cta.heading}</h2>
            <p className="leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: article.cta.html }} />
          </div>
          <Link to={article.cta.to} className="inline-flex items-center gap-2 bg-black text-volt font-head font-bold uppercase tracking-wide px-8 py-4 text-lg hover:scale-105 transition-transform shrink-0" data-testid="article-cta-button">
            {article.cta.label} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
